import { TransferRail } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  flowNameAttributes,
  TRANSFER_SUBMIT_STEP_NAME,
  transferFlowErrorEvent,
  UNKNOWN_ERROR_REASON,
} from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import type { TransferRecovery } from 'features/feeBasedInstantTransfer/hooks/useSubmitReviewedTransfer';
import { useTranslation } from 'react-i18next';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useViewDidFocusTrackAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import {
  ErrorScreen,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';

const TRANSFER_ERROR_SCREEN_KEY = 'instantTransferError';
const TRANSFER_ERROR_SOURCE_NAME = 'transfer_money_error';

type TransferErrorSearchParams = {
  rail?: string;
  recovery?: TransferRecovery;
  errorReason?: string;
  fromId?: string;
  amount?: string;
  flowName?: string;
};

function TransferErrorScreen() {
  const { t } = useTranslation('feeBasedInstantTransfer');
  const router = useRouter();
  const { defaultTitle, defaultMessage, doneButtonTitle } =
    useDefaultErrorStrings();
  const { rail, recovery, errorReason, fromId, amount, flowName } =
    useLocalSearchParams<TransferErrorSearchParams>();
  const analyticsErrorReason = errorReason ?? UNKNOWN_ERROR_REASON;
  const flowAttributes = flowNameAttributes(flowName);
  const canChooseDifferentAccount = recovery === 'chooseDifferentAccount';
  const isDebitCardRejection =
    canChooseDifferentAccount && rail === TransferRail.PushToCard;

  useViewDidFocusTrackAnalyticsEvent(
    transferFlowErrorEvent(
      TRANSFER_ERROR_SOURCE_NAME,
      analyticsErrorReason,
      flowName,
    ),
  );

  const handleChooseDifferentAccount = () => {
    // The entry reads its preselection once on mount, so it must remount to drop an Advance lock.
    router.replace({
      pathname: '/move-money/transfer/instant-transfer',
      params: { fromId, amount, flowName },
    });
  };

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={navigateBack}
              primaryIcon={
                <Icon
                  iconKey="CLOSE"
                  iconSize="MD"
                  iconAccessibilityLabel={t('error.close')}
                />
              }
            />
          ),
        }}
      />
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        screenAnalyticsEventKey={TRANSFER_ERROR_SCREEN_KEY}
        customAttributes={{
          step_name: TRANSFER_SUBMIT_STEP_NAME,
          error_reason: analyticsErrorReason,
          ...flowAttributes,
        }}
        messageHeadline={isDebitCardRejection ? t('error.title') : defaultTitle}
        messageBody={
          isDebitCardRejection ? t('error.debitCardMessage') : defaultMessage
        }
        primaryButton={
          canChooseDifferentAccount
            ? {
                title: t('error.chooseDifferentAccount'),
                onPress: handleChooseDifferentAccount,
                analytics: {
                  analyticsId: 'transferMoneyErrorChooseDifferentAccount',
                  customAttributes: flowAttributes,
                },
              }
            : undefined
        }
        secondaryButton={{
          title: doneButtonTitle,
          onPress: navigateBack,
          analytics: {
            analyticsId: 'transferMoneyErrorDone',
            customAttributes: flowAttributes,
          },
        }}
      />
    </>
  );
}

export default TransferErrorScreen;
